import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { NgClass, NgIf } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ResizableModule, ResizeEvent } from "angular-resizable-element";
import { StatsData } from "../../models/stats-response.interface";
import {
  TableBulkActionEvent,
  TableBulkActionOption,
  TableMenuOption,
  TableMeta
} from "../../models/table-meta.interface";
import { PaginatedListDataResponse } from "../../models/paginated-list-data-response";
import {
  FilterCondition,
  FilterConfig,
  SearchBarConfig,
  TableListRequest,
  ViewConfig,
  ViewItem,
  Where
} from "../../models/table-list-request.interface";
import { User } from "../../models/user.interface";
import { LoadingService } from "../../services/loading.service";
import { RightPanelComponent } from "../right-panel/right-panel.component";
import { AdvancedFilterComponentV2 } from "../advanced-filter/advanced-filter-v2.component";
import { FieldInfo } from "../../models/editable-table-header.interface";
import { CommonUtils } from "../../utils/common-utils";
import { MultiSelectComponent } from "../select/multi-select.component";
import { NzBadgeComponent } from "ng-zorro-antd/badge";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { FILTER_QUERY_PARAM_KEY, HeaderService } from "../../services/header.service";
import { filter, switchMap } from "rxjs/operators";
import { merge, Subject, takeUntil, tap } from "rxjs";
import { AgGridWrapperComponent } from "../ag-grid-wrapper/ag-grid-wrapper.component";
import { RouteFilterStorageService } from "../../services/route-filter-storage.service";

interface PersistedTosTableFilterState {
  searchTerm: string;
  previousTerm: string;
  rawFilterData: Where | null;
  filterData: Where | null;
  searchFilterData: Where | null;
  isFilterApplied: boolean;
}


@Component({
  selector: "app-tos-table",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    ResizableModule,
    NgClass,
    MultiSelectComponent,
    NzBadgeComponent,
    AgGridWrapperComponent
  ],
  templateUrl: "./tos-table.component.html",
  styleUrl: "./tos-table.component.scss"
})
export class TosTableComponent implements AfterViewInit, OnInit, OnChanges {
  private destroy$ = new Subject<void>();
  private activeRoutePath: string = "";

  inputWidth = 5;
  totalPages: number = 0;
  openMenuIndex: number | null = null;
  customPageInput: number = 1;
  debounceTimer: any = null;
  isLoading: boolean = false;
  // Filter And Search states
  searchTerm: any = "";
  previousTerm: string = "";
  rawFilterData: Where | null = null;
  isFilterApplied: boolean = false;
  filterData: Where | null = null;
  searchFilterData!: Where | null;
  rowsPerPageOptions: { label: number, value: number }[] = [];
  preSetFilters: Where | null = null; // This is used to apply filters from query params


  @Input() selectedItems: any[] = [];
  @Input() stats!: StatsData;
  @Input() tableMeta!: TableMeta;
  @Input() dataListRequest!: TableListRequest;
  @Input() dataListResponse!: PaginatedListDataResponse;
  @Input() showTableStats: boolean = true;
  @Input() tableMenuOptions: TableMenuOption[] = [];
  @Input() bulkActionOptions: TableBulkActionOption[] = [];
  @Input() showMenuOptions: boolean = true;
  @Input() allTableRecordsSelected: boolean = false;
  @Input() searchBarConfig?: SearchBarConfig;
  @Input() filterConfig?: FilterConfig;
  @Input() defaultFilters?: Where;
  @Input() viewFilters?: Where;
  @Input() viewConfig?: ViewConfig;

  @Output() dataListRequestChange = new EventEmitter<TableListRequest>();
  @Output() tosTableAction = new EventEmitter<any>();
  @Output() rowActionTriggered = new EventEmitter<any>();
  @Output() selectedItemsChange = new EventEmitter<any[]>();
  @Output() allTableRecordsSelectedChange = new EventEmitter<boolean>();
  @Output() tableMenuOptionSelected = new EventEmitter<any>();
  @Output() bulkActionTriggered = new EventEmitter<TableBulkActionEvent>();
  @Output() onViewChange = new EventEmitter<ViewItem>();

  @ViewChild("widthCalculator") widthCalculator!: ElementRef;

  @ViewChild("viewselector", { static: false }) viewSelector!: MultiSelectComponent;

  constructor(private cdr: ChangeDetectorRef,
              private loadingService: LoadingService,
              private viewContainerRef: ViewContainerRef,
              private route: ActivatedRoute,
              private router: Router,
              private headerService: HeaderService,
              private routeFilterStorageService: RouteFilterStorageService) {
  }

  handleSelectedItemsChange(items: any[]) {
    this.selectedItems = this.mergeSelectedItemsAcrossPages(items ?? []);
    if (this.allTableRecordsSelected) {
      this.allTableRecordsSelected = false;
      this.allTableRecordsSelectedChange.emit(false);
    }
    this.selectedItemsChange.emit(this.selectedItems);
  }

  clearSelectedRecords(): void {
    this.selectedItems = [];
    if (this.allTableRecordsSelected) {
      this.allTableRecordsSelected = false;
      this.allTableRecordsSelectedChange.emit(false);
    }
    this.selectedItemsChange.emit(this.selectedItems);
  }

  selectAllTableRecords(): void {
    if (!this.dataListResponse?.totalCount) {
      return;
    }

    this.allTableRecordsSelected = true;
    this.allTableRecordsSelectedChange.emit(true);
  }

  canSelectAllTableRecords(): boolean {
    const selectedCount = this.selectedItems?.length ?? 0;
    const totalCount = this.dataListResponse?.totalCount ?? 0;

    return !this.allTableRecordsSelected && selectedCount > 0 && totalCount > selectedCount;
  }

  getSelectedRecordsCount(): number {
    if (this.allTableRecordsSelected && this.dataListResponse?.totalCount) {
      return this.dataListResponse.totalCount;
    }

    return this.selectedItems?.length ?? 0;
  }

  shouldShowBulkActionSection(): boolean {
    const hasBulkActions = (this.bulkActionOptions?.length ?? 0) > 0;
    const hasSelection = this.allTableRecordsSelected || (this.selectedItems?.length ?? 0) > 0;
    return hasBulkActions && hasSelection;
  }

  onBulkActionClick(action: TableBulkActionOption): void {
    this.bulkActionTriggered.emit({
      action,
      selectedItems: [...(this.selectedItems ?? [])],
      allTableRecordsSelected: this.allTableRecordsSelected
    });
  }

  ngOnInit() {
    this.activeRoutePath = this.normalizeRoute(this.router.url);
    this.restoreFilterState();
    this.generateRowsPerPageOptions();
    this.loadingService.loading$
      .subscribe({
        next: data => {
          this.isLoading = data.isLoading && data.type === "inline";
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLoading = false;
        }
      });

    merge(
      this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)),
      this.route.queryParamMap
    )
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.handleRouteScopeChange()),
        switchMap(() => this.setPreFilterFilterBasedOnQueryParam()), // handle async directly
        tap(() => this.setPageFilters()) // do side-effect
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.updateInputWidth();
    // this.setPageFilters();
  }

  ngOnDestroy() {
    console.log(`TosTableComponent destroyed ${this.router.url}`);
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["dataListResponse"] &&
      changes["dataListResponse"].currentValue
    ) {
      this.calculateTotalPages();
      this.customPageInput = this.dataListRequest.page + 1;
    }
  }

  handlePreviousBtnClick() {
    if (!(this.dataListRequest.page > 0)) return;

    this.dataListRequest.page -= 1;
    this.tosTableAction.emit();
  }

  handleNextButtonClick() {
    if (
      !(
        this.dataListRequest.page >= 0 &&
        this.dataListRequest.page < this.totalPages - 1
      )
    )
      return;

    this.dataListRequest.page += 1;
    this.tosTableAction.emit();
  }

  handleRowsPerPage(event: any) {
    const selectedValue = event?.value;
    this.dataListRequest.limit = Number(selectedValue);
    this.dataListRequest.page = 0;
    this.tosTableAction.emit();
  }

  onResizeEnd(event: ResizeEvent, column: string): void {
    if (event.edges.right) {
      // Use attribute selectors to handle column names with dots
      const columnHeader = document.querySelector(
        `th[class*="${column}_th"]`
      ) as HTMLElement;
      const columnCells = document.querySelectorAll(
        `td[class*="${column}_td"]`
      );
      const newWidth = `${event.rectangle.width}px`;

      if (columnHeader) {
        columnHeader.style.width = newWidth;
      }

      columnCells.forEach((cell) => {
        const cellHtml = cell as HTMLElement;
        cellHtml.style.width = newWidth;
      });
    }
  }

  sortData(column: string) {
    if (!this.dataListRequest.orderBy) {
      this.dataListRequest.orderBy = {};
    }

    const currentOrder = this.dataListRequest.orderBy[column];

    if (!currentOrder) {
      this.dataListRequest.orderBy = {
        [column]: "desc",
        ...this.dataListRequest.orderBy
      };
    } else if (currentOrder === "desc") {
      this.dataListRequest.orderBy[column] = "asc";
    } else if (currentOrder === "asc") {
      delete this.dataListRequest.orderBy[column];
    }

    this.tosTableAction.emit();
  }

  calculateTotalPages() {
    if (this.dataListResponse.totalCount) {
      this.totalPages = Math.ceil(
        this.dataListResponse.totalCount / this.dataListRequest.limit
      );
    } else if (this.stats) {
      this.totalPages = Math.ceil(
        this.stats.total / this.dataListRequest.limit
      );
    }
  }

  orderByListContainsField(fieldName: string): any {
    return (
      this.dataListRequest.orderBy &&
      this.dataListRequest.orderBy.hasOwnProperty(fieldName)
    );
  }

  toggleSelection(record: any) {
    console.log("toggle: ", this.selectedItems);
    if (!this.isSelected(record)) {
      this.selectedItems.push(record);
    } else {
      const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
      this.selectedItems = this.selectedItems.filter((i) =>
        uniqueColumns.length > 0
          ? !uniqueColumns.every((key) => i?.[key] === record?.[key])
          : i?.id !== record?.id
      );
      if (this.allTableRecordsSelected) {
        this.allTableRecordsSelected = false;
        this.allTableRecordsSelectedChange.emit(false);
      }
    }
    this.selectedItemsChange.emit(this.selectedItems);
  }

  isSelected(item: any): boolean {
    if (this.allTableRecordsSelected) {
      return true;
    }

    const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
    if (uniqueColumns.length > 0) {
      return this.selectedItems.some((x) =>
        uniqueColumns.every((key) => x[key] === item[key])
      );
    }

    return this.selectedItems.some((x) => x.id === item.id);
  }

  isAllRecordsOfCurrentPageSelected(): boolean {
    return (
      this.allTableRecordsSelected ||
      (this.dataListResponse.records?.length > 0 &&
        this.dataListResponse.records?.every((item: User) =>
          this.isSelected(item)
        ))
    );
  }

  toggleAllRecordsOfCurrentPageSelections(isChecked: boolean) {
    if (isChecked) {
      this.dataListResponse.records?.forEach((item) => {
        if (!this.isSelected(item)) {
          this.selectedItems.push(item);
        }
      });
    } else {
      const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
      this.dataListResponse.records?.forEach((item) => {
        this.selectedItems = this.selectedItems.filter((i) =>
          uniqueColumns.length > 0
            ? !uniqueColumns.every((key) => i?.[key] === item?.[key])
            : i?.id !== item?.id
        );
      });
    }

    this.selectedItemsChange.emit(this.selectedItems);
  }

  handleMenuToggle(index: number) {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  handleOptionSelected(record: any, actionName: any) {
    this.tableMenuOptionSelected.emit({ record, actionName });
  }

  handleInlineAction(event: any) {
    this.rowActionTriggered.emit(event);
  }

  getNestedValue(record: any, fieldName: string): any {
    if (!fieldName.includes(".")) {
      return record[fieldName];
    }
    return fieldName.split(".").reduce((acc, part) => acc && acc[part], record);
  }

  protected readonly input = input;


  onDebouncedPageInputChange(): void {
    //basic debounced used so that does make api does not call at every change
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      const page = Number(this.customPageInput);

      if (!isNaN(page) && page >= 1 && page <= this.totalPages) {
        this.dataListRequest.page = page - 1;
        this.tosTableAction.emit();
      } else {
        this.customPageInput = this.dataListRequest.page + 1; // reset to valid value
      }
    }, 300);
    this.updateInputWidth();
  }

  updateInputWidth() {
    //This Function is used for dynamic input size increasing width of pagination input box this i made because was not using ngModel
    if (this.widthCalculator) {
      const spanWidth = this.widthCalculator.nativeElement.offsetWidth;
      this.inputWidth = Math.max(30, spanWidth + 10); // padding
    }
  }

  handleTableSearch(event: KeyboardEvent) {
    const searchTerm = this.searchTerm.trim();

    // this.searchFilterData = this.searchBarConfig?.filters

    if (searchTerm.length > 0) {
      const filterConditions = this.getFilterConditionsFromSearchBarConfig(searchTerm);
      const expression = this.getExpressionForConditions(filterConditions, "OR");
      this.searchFilterData = {
        filterConditions,
        expression
      };

      if (event.key === "Enter") {
        this.mergeFilters();
        this.persistFilterState();
      }
    }

    if (searchTerm.length === 0 && this.previousTerm.length !== 0) {
      this.searchFilterData = null;
      this.mergeFilters();
      this.persistFilterState();
    }
    this.previousTerm = searchTerm;
  }

  filter() {
    const rightPanelComponentComponentRef = this.viewContainerRef.createComponent(RightPanelComponent);
    rightPanelComponentComponentRef.instance.title = "Advanced Filter";
    rightPanelComponentComponentRef.instance.showActionBtns = true;
    rightPanelComponentComponentRef.instance.primaryActionBtnText = "Apply Filter";


    const fieldInfoList: FieldInfo[] = CommonUtils.getFilterFieldList(this.filterConfig?.filterFields ?? []);
    const filterDialogRef: ComponentRef<AdvancedFilterComponentV2> = rightPanelComponentComponentRef.instance.loadComponent(
      AdvancedFilterComponentV2, {
        filterFields: fieldInfoList,
        appliedFilters: this.getAppliedFiltersForPanel(),
        useNewContract: this.filterConfig?.useNewContract ?? false,
        view: "panelView"
      }
    );

    rightPanelComponentComponentRef.instance.primaryActionBtnEmitter.subscribe(action => {
      filterDialogRef.instance.applyFilter();
    });

    rightPanelComponentComponentRef.instance.close.subscribe((res: any) => {
      this.selectedItems = [];
      if (res && res.resetAll) {
        this.rawFilterData = null;
        this.filterData = null;
        this.isFilterApplied = false;
        this.mergeFilters();
        this.clearPersistedFilterState();
      } else if (res && res.refresh) {
        if (res.filterData.expression && res.filterData.filterConditions.length > 0) {
          this.filterData = {
            filterConditions: res.filterData.filterConditions.map((condition: any) => {
              return {
                ...condition,
                value: condition.value.map((item: any) => item.key ?? item)
              };
            }),
            expression: res.filterData.expression
          };
          this.isFilterApplied = true;
          this.rawFilterData = res.rawFilterData;
          this.mergeFilters();
          this.persistFilterState();
        } else {
          console.log("Invalid filter !");
          this.rawFilterData = null;
          this.filterData = null;
          this.isFilterApplied = false;
          this.mergeFilters();
          this.clearPersistedFilterState();
        }
      }

      rightPanelComponentComponentRef.destroy();
    });

  }

  mergeFilters() {
    this.dataListRequest.where = { filterConditions: [], expression: "" };

    const shouldMergePreSetSeparately = this.shouldMergePreSetFilters(this.filterData);
    const mergedFilterConditions: FilterCondition[] = [];

    this.addUniqueFilterConditions(mergedFilterConditions, this.defaultFilters?.filterConditions);
    this.addUniqueFilterConditions(mergedFilterConditions, this.filterData?.filterConditions);
    this.addUniqueFilterConditions(mergedFilterConditions, this.searchFilterData?.filterConditions);
    this.addUniqueFilterConditions(mergedFilterConditions, this.viewFilters?.filterConditions);
    if (shouldMergePreSetSeparately) {
      this.addUniqueFilterConditions(mergedFilterConditions, this.preSetFilters?.filterConditions);
    }

    this.dataListRequest.where.filterConditions = mergedFilterConditions;

    const expressions: string[] = [];
    if (this.filterData?.expression) {
      expressions.push(`${this.filterData.expression}`);
    }
    if (this.searchFilterData?.expression) {
      expressions.push(`(${this.searchFilterData.expression})`);
    }
    if (this.defaultFilters?.expression) {
      expressions.push(`(${this.defaultFilters?.expression})`);
    }
    if (this.viewFilters?.expression) {
      expressions.push(`(${this.viewFilters?.expression})`);
    }
    if (this.preSetFilters?.expression && shouldMergePreSetSeparately) {
      expressions.push(`(${this.preSetFilters?.expression})`);
    }

    // Safely assign the joined expression
    this.dataListRequest.where.expression = expressions.join(" AND ");
    this.dataListRequest.page = 0; //reset to page 0
    this.totalPages = 1;
    this.tosTableAction.emit();
  }

  private getExpressionForConditions(filterConditions: FilterCondition[], joinString: "OR" | "AND"): string {
    return filterConditions
      .map(config => config.alias)
      .join(` ${joinString} `);
  }

  private getFilterConditionsFromSearchBarConfig(searchTerm: string): FilterCondition[] {
    return this.searchBarConfig?.filters.map((config): FilterCondition => {
      return {
        ...config,
        value: [searchTerm]
      };
    }) ?? [];
  }

  private setPageFilters(): void {
    if (this.viewConfig?.viewOptions) {
      this.viewSelector.toggleSelection(this.viewConfig.viewOptions[0]);
    } else {
      this.mergeFilters();
    }
  }

  handleViewChange(selectedView: ViewItem): void {
    const filterConditions = selectedView.filters;
    const expression = this.getExpressionForConditions(filterConditions, "AND");
    this.viewFilters = {
      filterConditions,
      expression
    };
    this.mergeFilters();
    this.onViewChange.emit(selectedView);
  }

  generateRowsPerPageOptions() {
    const defaultOptions = [50, 100];
    const selectableOptions = [10, 15, 25];

    const options = new Set<number>();

    if (selectableOptions.includes(this.dataListRequest?.limit)) {
      options.add(this.dataListRequest.limit);
    }

    defaultOptions.forEach(opt => options.add(opt));

    const sorted = Array.from(options).sort((a, b) => a - b);
    this.rowsPerPageOptions = sorted.map(val => ({
      label: val,
      value: val
    }));
  }

  private setPreFilterFilterBasedOnQueryParam() {
    return new Promise(resolve => {
      const filterStr = this.route.snapshot.queryParamMap.get(FILTER_QUERY_PARAM_KEY);
      if (!filterStr) {
        this.preSetFilters = null;
        resolve(filterStr);
        return;
      }

      try {
        const preSetFilter = JSON.parse(filterStr);
        this.preSetFilters = this.headerService.getRawFiltersWithValuesInjected(preSetFilter);
      } catch (error) {
        this.preSetFilters = null;
        console.error("Invalid preset filter query params", error);
      }

      resolve(filterStr);
    });
  }

  getAppliedFilterCount(): number {
    const mergedFilterConditions: FilterCondition[] = [];
    this.addUniqueFilterConditions(mergedFilterConditions, this.rawFilterData?.filterConditions);

    const shouldMergePreSetSeparately = this.shouldMergePreSetFilters(this.rawFilterData);
    if (shouldMergePreSetSeparately) {
      this.addUniqueFilterConditions(mergedFilterConditions, this.preSetFilters?.filterConditions);
    }

    return mergedFilterConditions.length;
  }

  private getAppliedFiltersForPanel(): Where | null {
    const mergedFilterConditions: FilterCondition[] = [];
    this.addUniqueFilterConditions(mergedFilterConditions, this.rawFilterData?.filterConditions);

    const shouldMergePreSetSeparately = this.shouldMergePreSetFilters(this.rawFilterData);
    if (shouldMergePreSetSeparately) {
      this.addUniqueFilterConditions(mergedFilterConditions, this.preSetFilters?.filterConditions);
    }

    if (mergedFilterConditions.length === 0) {
      return null;
    }

    const expressions: string[] = [];
    if (this.rawFilterData?.expression) {
      expressions.push(`(${this.rawFilterData.expression})`);
    }

    if (this.preSetFilters?.expression && shouldMergePreSetSeparately) {
      expressions.push(`(${this.preSetFilters.expression})`);
    }

    return {
      filterConditions: CommonUtils.deepCopyJson(mergedFilterConditions),
      expression: expressions.join(" AND ") || mergedFilterConditions.map((condition) => condition.alias).join(" AND ")
    };
  }

  private addUniqueFilterConditions(target: FilterCondition[], source?: FilterCondition[] | null): void {
    if (!source?.length) {
      return;
    }

    const existingFilterKeys = new Set(target.map((condition) => this.getFilterConditionKey(condition)));
    source.forEach((condition) => {
      const filterKey = this.getFilterConditionKey(condition);
      if (!existingFilterKeys.has(filterKey)) {
        target.push(condition);
        existingFilterKeys.add(filterKey);
      }
    });
  }

  private shouldMergePreSetFilters(existingFilters?: Where | null): boolean {
    const preSetFilterAliases = (this.preSetFilters?.filterConditions ?? [])
      .map((condition) => condition.alias)
      .filter((alias): alias is string => !!alias);

    if (!preSetFilterAliases.length) {
      return false;
    }

    const existingAliases = new Set(
      (existingFilters?.filterConditions ?? [])
        .map((condition) => condition.alias)
        .filter((alias): alias is string => !!alias)
    );

    return !preSetFilterAliases.some((alias) => existingAliases.has(alias));
  }

  private getFilterConditionKey(condition: FilterCondition): string {
    if (condition.alias) {
      return `alias:${condition.alias}`;
    }

    return `${condition.name}|${condition.operator ?? ""}|${JSON.stringify(condition.value ?? [])}`;
  }

  private mergeSelectedItemsAcrossPages(currentPageSelection: any[]): any[] {
    const selectedItemsMap = new Map<string, any>();
    this.selectedItems.forEach((item) => {
      selectedItemsMap.set(this.getRecordKey(item), item);
    });

    const currentPageKeys = new Set(
      (this.dataListResponse?.records ?? []).map((item) => this.getRecordKey(item))
    );
    currentPageKeys.forEach((key) => selectedItemsMap.delete(key));

    currentPageSelection.forEach((item) => {
      selectedItemsMap.set(this.getRecordKey(item), item);
    });

    return Array.from(selectedItemsMap.values());
  }

  private getRecordKey(record: any): string {
    const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
    if (uniqueColumns.length > 0) {
      const compositeKey = uniqueColumns
        .map((key) => `${key}:${this.getNestedValue(record, key)}`)
        .join("|");
      return `uk:${compositeKey}`;
    }

    if (record?.id !== undefined && record?.id !== null) {
      return `id:${record.id}`;
    }

    if (record?.randomId !== undefined && record?.randomId !== null) {
      return `randomId:${record.randomId}`;
    }

    return JSON.stringify(record);
  }

  private restoreFilterState(): void {
    const persistedState = this.routeFilterStorageService.get<PersistedTosTableFilterState>(this.getFilterStorageScope());
    if (!persistedState) {
      return;
    }

    this.searchTerm = persistedState.searchTerm;
    this.previousTerm = persistedState.previousTerm;
    this.rawFilterData = persistedState.rawFilterData;
    this.filterData = persistedState.filterData;
    this.searchFilterData = persistedState.searchFilterData;
    this.isFilterApplied = persistedState.isFilterApplied;
  }

  private handleRouteScopeChange(): void {
    const nextRoutePath = this.normalizeRoute(this.router.url);
    if (nextRoutePath === this.activeRoutePath) {
      return;
    }

    this.activeRoutePath = nextRoutePath;
    this.resetFilterState();
    this.restoreFilterState();
  }

  private resetFilterState(): void {
    this.searchTerm = "";
    this.previousTerm = "";
    this.rawFilterData = null;
    this.filterData = null;
    this.searchFilterData = null;
    this.isFilterApplied = false;
    this.preSetFilters = null;
    this.clearSelectedRecords();
  }

  private persistFilterState(): void {
    const stateToPersist: PersistedTosTableFilterState = {
      searchTerm: this.searchTerm,
      previousTerm: this.previousTerm,
      rawFilterData: this.rawFilterData,
      filterData: this.filterData,
      searchFilterData: this.searchFilterData,
      isFilterApplied: this.isFilterApplied
    };

    this.routeFilterStorageService.set(this.getFilterStorageScope(), stateToPersist);
  }

  private clearPersistedFilterState(): void {
    this.routeFilterStorageService.remove(this.getFilterStorageScope());
  }

  private getFilterStorageScope(): string {
    const tableName = this.tableMeta?.name?.trim();
    if (tableName) {
      return `tos-table::${tableName}`;
    }

    const filterFieldScope = this.filterConfig?.filterFields?.map(field => field.name).join("|");
    if (filterFieldScope) {
      return `tos-table::${filterFieldScope}`;
    }

    const searchScope = this.searchBarConfig?.filters?.map(filterConfig => filterConfig.alias).join("|");
    return `tos-table::${searchScope || "default"}`;
  }

  private normalizeRoute(url: string): string {
    const routeWithoutQueryAndHash = url.split(/[?#]/)[0] || "/";
    if (routeWithoutQueryAndHash === "/") {
      return routeWithoutQueryAndHash;
    }

    return routeWithoutQueryAndHash.endsWith("/")
      ? routeWithoutQueryAndHash.slice(0, -1)
      : routeWithoutQueryAndHash;
  }
}
